import { useState, useEffect } from 'react'
import { apiUrl, authFetch } from '../../lib/api'
import { TicketModal } from './TicketModal'

const PULSE_FEATURES = [
  { id: 'ai_chat', label: 'AI Chat', desc: 'AI chat panel (ask/edit/agent modes)' },
  { id: 'ai_agent', label: 'AI Agent', desc: 'Autonomous agent that reads/writes/runs commands' },
  { id: 'ai_image_gen', label: 'AI Image Generation', desc: 'Generate images from text prompts' },
  { id: 'ai_autocomplete', label: 'AI Autocomplete', desc: 'Ghost text inline code completions' },
  { id: 'voice_coding', label: 'Voice Coding', desc: 'Voice commands and wake word detection' },
  { id: 'code_editor', label: 'Code Editor', desc: 'Full CodeMirror editor with syntax highlighting' },
  { id: 'terminal', label: 'Terminal', desc: 'Integrated terminal with shell access' },
  { id: 'file_explorer', label: 'File Explorer', desc: 'File tree with create/rename/delete' },
  { id: 'live_preview', label: 'Live Preview', desc: 'Real-time app preview with dev server' },
  { id: 'deploy', label: 'Deploy', desc: 'Deploy to Cloudflare/Vercel/Netlify' },
  { id: 'custom_domain', label: 'Custom Domains', desc: 'Custom domain mapping for deployments' },
  { id: 'collab', label: 'Real-time Collab', desc: 'Multi-user editing with live cursors' },
  { id: 'git_integration', label: 'Git Integration', desc: 'Git status, diff, commit, push' },
  { id: 'database_panel', label: 'Database Panel', desc: 'Visual database browser and editor' },
  { id: 'secrets_manager', label: 'Secrets Manager', desc: 'Environment variables management' },
  { id: 'package_manager', label: 'Package Manager', desc: 'Visual npm package installer' },
  { id: 'code_review', label: 'Code Review', desc: 'AI-powered code review and suggestions' },
  { id: 'test_generation', label: 'Test Generation', desc: 'Auto-generate unit/integration tests' },
  { id: 'docs_generation', label: 'Docs Generation', desc: 'Auto-generate documentation' },
  { id: 'security_scan', label: 'Security Scan', desc: 'Vulnerability and secrets scanning' },
  { id: 'templates', label: 'Templates', desc: 'Project templates gallery' },
  { id: 'community', label: 'Community', desc: 'Public project sharing and explore page' },
  { id: 'teams', label: 'Teams', desc: 'Team workspaces with roles' },
  { id: 'analytics', label: 'Analytics', desc: 'Project analytics and visitor tracking' },
  { id: 'cron_jobs', label: 'Cron Jobs', desc: 'Scheduled task automation' },
  { id: 'ci_cd', label: 'CI/CD', desc: 'Continuous integration and deployment pipelines' },
  { id: 'debugger', label: 'Debugger', desc: 'Multi-language debugger with breakpoints' },
  { id: 'extensions', label: 'Extensions', desc: 'Extension marketplace and plugins' },
  { id: 'themes', label: 'Themes', desc: 'Custom editor themes' },
  { id: 'version_history', label: 'Version History', desc: 'File version timeline with restore' },
  { id: 'referrals', label: 'Referral System', desc: 'Referral codes and credits' },
  { id: 'billing_dashboard', label: 'Billing Dashboard', desc: 'Usage charts and invoice history' },
  { id: 'sso', label: 'SSO (SAML/OIDC)', desc: 'Enterprise single sign-on' },
  { id: 'audit_logs', label: 'Audit Logs', desc: 'Enterprise activity audit trail' },
  { id: 'canvas', label: 'Canvas/Whiteboard', desc: 'Drawing canvas with AI code generation' },
  { id: 'figma_import', label: 'Figma Import', desc: 'Import designs from Figma' },
]

interface User {
  id: string
  email: string
  name: string
  plan: string
  role: string
  created_at: string
  requests_today?: number
  tokens_in_today?: number
  tokens_out_today?: number
}

interface Stats {
  totalUsers: number
  activeToday: number
  totalProjects: number
  totalDeployments: number
  totalAIRequests: number
  revenue: number
}

export function AdminDashboard() {
  const [users, setUsers] = useState<User[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<'overview' | 'users' | 'usage' | 'revenue' | 'health' | 'deployments' | 'flags' | 'reports' | 'promos' | 'tickets' | 'announce' | 'audit'>('overview')
  const [auditLogs, setAuditLogs] = useState<any[]>([])
  const [revenue, setRevenue] = useState<any>(null)
  const [aiUsage, setAiUsage] = useState<any>(null)
  const [health, setHealth] = useState<any>(null)
  const [deployments, setDeployments] = useState<any[]>([])
  const [userDetail, setUserDetail] = useState<any>(null)
  const [announceTitle, setAnnounceTitle] = useState('')
  const [announceBody, setAnnounceBody] = useState('')
  const [announcePlan, setAnnouncePlan] = useState('all')
  const [dbTable, setDbTable] = useState<string | null>(null)
  const [dbRows, setDbRows] = useState<any[]>([])
  const [dbColumns, setDbColumns] = useState<string[]>([])
  const [dbLoading, setDbLoading] = useState(false)
  const [flags, setFlags] = useState<any[]>([])
  const [reports, setReports] = useState<any[]>([])
  const [newFlagName, setNewFlagName] = useState('')
  const [newFlagPlans, setNewFlagPlans] = useState('all')
  const [newFlagDesc, setNewFlagDesc] = useState('')
  const [promos, setPromos] = useState<any[]>([])
  const [newPromoCode, setNewPromoCode] = useState('')
  const [newPromoDiscount, setNewPromoDiscount] = useState('20')
  const [newPromoMaxUses, setNewPromoMaxUses] = useState('100')
  const [tickets, setTickets] = useState<any[]>([])
  const [selectedTicket, setSelectedTicket] = useState<any>(null)

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [statsRes, usersRes] = await Promise.all([
        authFetch(apiUrl('/api/enterprise/stats')),
        authFetch(apiUrl('/api/enterprise/users')),
      ])
      const statsData = await statsRes.json()
      const usersData = await usersRes.json()
      setStats(statsData)
      setUsers(Array.isArray(usersData) ? usersData : usersData.users || [])
    } catch {}
    setLoading(false)
  }

  const loadAudit = async () => {
    try {
      const res = await authFetch(apiUrl('/api/enterprise/audit?limit=50'))
      const data = await res.json()
      setAuditLogs(Array.isArray(data) ? data : data.logs || [])
    } catch {}
  }

  const loadRevenue = async () => {
    try {
      const res = await authFetch(apiUrl('/api/enterprise/revenue'))
      setRevenue(await res.json())
    } catch {}
  }

  const loadAiUsage = async () => {
    try {
      const res = await authFetch(apiUrl('/api/enterprise/ai-usage'))
      setAiUsage(await res.json())
    } catch {}
  }

  const loadHealth = async () => {
    try {
      const res = await authFetch(apiUrl('/api/enterprise/health'))
      setHealth(await res.json())
    } catch {}
  }

  const loadFlags = async () => {
    try {
      const res = await authFetch(apiUrl('/api/enterprise/flags'))
      setFlags(await res.json())
    } catch {}
  }

  const loadReports = async () => {
    try {
      const res = await authFetch(apiUrl('/api/enterprise/reports'))
      setReports(await res.json())
    } catch {}
  }

  const toggleFlag = async (name: string, enabled: boolean) => {
    await authFetch(apiUrl('/api/enterprise/flags'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, enabled }),
    }).catch(() => {})
    loadFlags()
  }

  const addFlag = async () => {
    if (!newFlagName.trim()) return
    await authFetch(apiUrl('/api/enterprise/flags'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newFlagName, enabled: true, plans: newFlagPlans, description: newFlagDesc }),
    }).catch(() => {})
    setNewFlagName(''); setNewFlagDesc('')
    loadFlags()
  }

  const deleteFlag = async (name: string) => {
    if (!window.confirm(`Delete flag "${name}"?`)) return
    await authFetch(apiUrl(`/api/enterprise/flags/${name}`), { method: 'DELETE' }).catch(() => {})
    loadFlags()
  }

  const resolveReport = async (id: string, status: string) => {
    const note = prompt('Admin note (optional):')
    await authFetch(apiUrl(`/api/enterprise/reports/${id}`), {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, admin_note: note || '' }),
    }).catch(() => {})
    loadReports()
  }

  const loadPromos = async () => {
    try {
      const res = await authFetch(apiUrl('/api/enterprise/promos'))
      setPromos(await res.json())
    } catch {}
  }

  const addPromo = async () => {
    if (!newPromoCode.trim()) return
    await authFetch(apiUrl('/api/enterprise/promos'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: newPromoCode, discount_pct: Number(newPromoDiscount), max_uses: Number(newPromoMaxUses) }),
    }).catch(() => {})
    setNewPromoCode('')
    loadPromos()
  }

  const deletePromo = async (id: string) => {
    if (!window.confirm('Delete this promo code?')) return
    await authFetch(apiUrl(`/api/enterprise/promos/${id}`), { method: 'DELETE' }).catch(() => {})
    loadPromos()
  }

  const loadTickets = async () => {
    try {
      const res = await authFetch(apiUrl('/api/enterprise/tickets'))
      setTickets(await res.json())
    } catch {}
  }

  const replyTicket = async (id: string) => {
    const reply = prompt('Your reply to the user:')
    if (!reply) return
    await authFetch(apiUrl(`/api/enterprise/tickets/${id}`), {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ admin_reply: reply, status: 'in_progress' }),
    }).catch(() => {})
    loadTickets()
  }

  const closeTicket = async (id: string) => {
    await authFetch(apiUrl(`/api/enterprise/tickets/${id}`), {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'closed' }),
    }).catch(() => {})
    loadTickets()
  }

  const setPriority = async (id: string, priority: string) => {
    await authFetch(apiUrl(`/api/enterprise/tickets/${id}`), {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ priority }),
    }).catch(() => {})
    loadTickets()
  }

  const loadDeployments = async () => {
    try {
      const res = await authFetch(apiUrl('/api/enterprise/deployments'))
      const data = await res.json()
      setDeployments(Array.isArray(data) ? data : [])
    } catch {}
  }

  const loadUserDetail = async (userId: string) => {
    try {
      const res = await authFetch(apiUrl(`/api/enterprise/users/${userId}`))
      setUserDetail(await res.json())
    } catch {}
  }

  const sendAnnouncement = async () => {
    if (!announceTitle.trim()) return
    await authFetch(apiUrl('/api/enterprise/announce'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: announceTitle, body: announceBody, targetPlan: announcePlan }),
    }).catch(() => {})
    setAnnounceTitle('')
    setAnnounceBody('')
    alert('Announcement sent!')
  }

  const loadTable = async (tableName: string) => {
    setDbTable(tableName)
    setDbLoading(true)
    try {
      const res = await authFetch(apiUrl('/api/enterprise/db/query'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql: `SELECT * FROM "${tableName}" ORDER BY created_at DESC LIMIT 50` }),
      })
      const data = await res.json()
      const rows = Array.isArray(data) ? data : data.rows || []
      setDbRows(rows)
      setDbColumns(rows.length > 0 ? Object.keys(rows[0]) : [])
    } catch {
      // Try without ORDER BY (some tables don't have created_at)
      try {
        const res = await authFetch(apiUrl('/api/enterprise/db/query'), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sql: `SELECT * FROM "${tableName}" LIMIT 50` }),
        })
        const data = await res.json()
        const rows = Array.isArray(data) ? data : data.rows || []
        setDbRows(rows)
        setDbColumns(rows.length > 0 ? Object.keys(rows[0]) : [])
      } catch { setDbRows([]); setDbColumns([]) }
    }
    setDbLoading(false)
  }

  const deleteRow = async (tableName: string, id: string) => {
    if (!window.confirm('Delete this row?')) return
    await authFetch(apiUrl('/api/enterprise/db/query'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sql: `DELETE FROM "${tableName}" WHERE id = '${id}'` }),
    }).catch(() => {})
    loadTable(tableName)
  }

  const changePlan = async (userId: string, plan: string) => {
    await authFetch(apiUrl(`/api/enterprise/users/${userId}/plan`), {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan }),
    }).catch(() => {})
    loadData()
  }

  const changeRole = async (userId: string, role: string) => {
    await authFetch(apiUrl(`/api/enterprise/users/${userId}/role`), {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    }).catch(() => {})
    loadData()
  }

  const banUser = async (userId: string) => {
    if (!window.confirm('Ban this user?')) return
    await changePlan(userId, 'banned')
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>Loading...</div>

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: '0 0 12px' }}>Admin Dashboard</h2>
        <div style={{ display: 'flex', gap: 6 }}>
          {(['overview', 'users', 'usage', 'revenue', 'health', 'deployments', 'flags', 'reports', 'promos', 'tickets', 'announce', 'audit'] as const).map(t => (
            <button key={t} onClick={() => { setTab(t); if (t === 'audit') loadAudit(); if (t === 'revenue') loadRevenue(); if (t === 'usage') loadAiUsage(); if (t === 'health') loadHealth(); if (t === 'deployments') loadDeployments(); if (t === 'flags') loadFlags(); if (t === 'reports') loadReports(); if (t === 'promos') loadPromos(); if (t === 'tickets') loadTickets() }}
              style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)', background: tab === t ? 'rgba(124,110,245,0.1)' : 'none', borderColor: tab === t ? 'rgba(124,110,245,0.3)' : undefined, color: tab === t ? '#7c6ef5' : 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 11, fontFamily: 'ui-monospace, monospace', fontWeight: 600, textTransform: 'capitalize' }}
            >{t}</button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        {tab === 'overview' && stats && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
              <StatCard label="Total Users" value={stats.totalUsers} color="#7c6ef5" />
              <StatCard label="Active Today" value={stats.activeToday} color="#5fd4a4" />
              <StatCard label="Projects" value={stats.totalProjects} color="#ffd086" />
              <StatCard label="Deployments" value={stats.totalDeployments} color="#38bdf8" />
              <StatCard label="AI Requests" value={stats.totalAIRequests} color="#f5a157" />
              <StatCard label="MRR" value={`$${stats.revenue || 0}`} color="#f07070" />
            </div>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 12 }}>Plan Distribution</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
              {['free', 'pro', 'unlimited', 'enterprise'].map(plan => {
                const count = users.filter(u => u.plan === plan).length
                const pct = users.length > 0 ? (count / users.length) * 100 : 0
                const colors: Record<string, string> = { free: '#666', pro: '#7c6ef5', unlimited: '#5fd4a4', enterprise: '#ffd086' }
                return (
                  <div key={plan} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 80, fontSize: 11, fontFamily: 'ui-monospace, monospace', color: 'rgba(255,255,255,0.6)' }}>{plan}</span>
                    <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.05)' }}>
                      <div style={{ height: '100%', borderRadius: 4, width: `${pct}%`, background: colors[plan] || '#666' }} />
                    </div>
                    <span style={{ width: 30, fontSize: 11, fontFamily: 'ui-monospace, monospace', color: 'rgba(255,255,255,0.4)', textAlign: 'right' }}>{count}</span>
                  </div>
                )
              })}
            </div>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 12 }}>Recent Users</h3>
            {users.slice(0, 5).map(u => (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 11 }}>
                <span style={{ flex: 1, color: '#fff', fontFamily: 'ui-monospace, monospace' }}>{u.email}</span>
                <span style={{ padding: '2px 8px', borderRadius: 4, background: 'rgba(124,110,245,0.1)', color: '#7c6ef5', fontSize: 9, fontWeight: 700 }}>{u.plan}</span>
                <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9 }}>{new Date(u.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </>
        )}

        {tab === 'users' && !userDetail && (
          <>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 12 }}>All Users ({users.length})</h3>
            <div style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'flex', padding: '8px 12px', background: 'rgba(255,255,255,0.03)', fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.4)', fontFamily: 'ui-monospace, monospace', textTransform: 'uppercase' }}>
                <span style={{ flex: 2 }}>Email</span>
                <span style={{ flex: 1 }}>Name</span>
                <span style={{ flex: 1 }}>Plan</span>
                <span style={{ flex: 1 }}>Role</span>
                <span style={{ flex: 1 }}>Joined</span>
                <span style={{ flex: 1 }}>Actions</span>
              </div>
              {users.map(u => (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderTop: '1px solid rgba(255,255,255,0.04)', fontSize: 11, color: 'rgba(255,255,255,0.7)', fontFamily: 'ui-monospace, monospace' }}>
                  <span style={{ flex: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#38bdf8', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => loadUserDetail(u.id)}>{u.email}</span>
                  <span style={{ flex: 1 }}>{u.name || '-'}</span>
                  <span style={{ flex: 1 }}>
                    <select value={u.plan} onChange={e => changePlan(u.id, e.target.value)} style={{ padding: '2px 4px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.7)', fontSize: 9, cursor: 'pointer' }}>
                      <option value="free">free</option>
                      <option value="pro">pro</option>
                      <option value="unlimited">unlimited</option>
                      <option value="enterprise">enterprise</option>
                    </select>
                  </span>
                  <span style={{ flex: 1 }}>
                    <select value={u.role || 'user'} onChange={e => changeRole(u.id, e.target.value)} style={{ padding: '2px 4px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.7)', fontSize: 9, cursor: 'pointer' }}>
                      <option value="user">user</option>
                      <option value="admin">admin</option>
                    </select>
                  </span>
                  <span style={{ flex: 1, fontSize: 9 }}>{new Date(u.created_at).toLocaleDateString()}</span>
                  <span style={{ flex: 1 }}>
                    <button onClick={() => banUser(u.id)} style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(240,112,112,0.3)', background: 'rgba(240,112,112,0.08)', color: '#f07070', fontSize: 9, cursor: 'pointer', fontWeight: 700 }}>Ban</button>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === 'users' && userDetail && (
          <>
            <button onClick={() => setUserDetail(null)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '4px 12px', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 11, fontFamily: 'ui-monospace, monospace', marginBottom: 16 }}>← Back to users</button>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{userDetail.user?.email}</h3>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontFamily: 'ui-monospace, monospace', marginBottom: 16 }}>
              Plan: <span style={{ color: '#7c6ef5' }}>{userDetail.user?.plan}</span> · Role: {userDetail.user?.role || 'user'} · Joined: {userDetail.user?.created_at ? new Date(userDetail.user.created_at).toLocaleDateString() : ''}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <h4 style={{ fontSize: 11, fontWeight: 700, color: '#fff', marginBottom: 6 }}>Projects ({userDetail.projects?.length || 0})</h4>
                {(userDetail.projects || []).map((p: any) => (
                  <div key={p.id} style={{ padding: '4px 0', fontSize: 10, fontFamily: 'ui-monospace, monospace', color: 'rgba(255,255,255,0.6)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>{p.name} <span style={{ color: 'rgba(255,255,255,0.3)' }}>({p.framework})</span></div>
                ))}
              </div>
              <div>
                <h4 style={{ fontSize: 11, fontWeight: 700, color: '#fff', marginBottom: 6 }}>Usage (last 7 days)</h4>
                {(userDetail.usage || []).map((u: any, i: number) => (
                  <div key={i} style={{ padding: '4px 0', fontSize: 10, fontFamily: 'ui-monospace, monospace', color: 'rgba(255,255,255,0.6)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>{u.date} — {u.requests} req, {u.tokens_in} in, {u.tokens_out} out</div>
                ))}
              </div>
              <div>
                <h4 style={{ fontSize: 11, fontWeight: 700, color: '#fff', marginBottom: 6 }}>Conversations ({userDetail.conversations?.length || 0})</h4>
                {(userDetail.conversations || []).map((c: any) => (
                  <div key={c.id} style={{ padding: '4px 0', fontSize: 10, fontFamily: 'ui-monospace, monospace', color: 'rgba(255,255,255,0.6)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>{c.title || 'Untitled'} <span style={{ color: 'rgba(255,255,255,0.3)' }}>({c.mode})</span></div>
                ))}
              </div>
              <div>
                <h4 style={{ fontSize: 11, fontWeight: 700, color: '#fff', marginBottom: 6 }}>Payments ({userDetail.payments?.length || 0})</h4>
                {(userDetail.payments || []).map((p: any, i: number) => (
                  <div key={i} style={{ padding: '4px 0', fontSize: 10, fontFamily: 'ui-monospace, monospace', color: 'rgba(255,255,255,0.6)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>${(p.amount_cents || 0) / 100} — <span style={{ color: p.status === 'succeeded' ? '#5fd4a4' : '#f07070' }}>{p.status}</span></div>
                ))}
              </div>
            </div>
            <h4 style={{ fontSize: 11, fontWeight: 700, color: '#fff', margin: '16px 0 6px' }}>Recent Activity</h4>
            {(userDetail.activity || []).slice(0, 10).map((a: any, i: number) => (
              <div key={i} style={{ display: 'flex', gap: 8, padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 10, fontFamily: 'ui-monospace, monospace' }}>
                <span style={{ padding: '1px 6px', borderRadius: 3, background: 'rgba(95,212,164,0.1)', color: '#5fd4a4', fontSize: 8, fontWeight: 700 }}>{a.action}</span>
                <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9 }}>{a.created_at ? new Date(a.created_at).toLocaleString() : ''}</span>
              </div>
            ))}
          </>
        )}

        {tab === 'usage' && (
          <>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 12 }}>AI Usage</h3>
            {!aiUsage ? <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', padding: 20 }}>Click to load usage data</div> : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
                  <StatCard label="Requests Today" value={aiUsage.today?.requests || 0} color="#7c6ef5" />
                  <StatCard label="Tokens In" value={`${Math.round((aiUsage.today?.tokensIn || 0) / 1000)}K`} color="#5fd4a4" />
                  <StatCard label="Tokens Out" value={`${Math.round((aiUsage.today?.tokensOut || 0) / 1000)}K`} color="#38bdf8" />
                  <StatCard label="Cost Today" value={`$${aiUsage.today?.cost || 0}`} color="#f07070" />
                  <StatCard label="Cost This Week" value={`$${aiUsage.week?.totalCost || 0}`} color="#f5a157" />
                </div>
                <h4 style={{ fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Last 7 Days</h4>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 100, marginBottom: 24 }}>
                  {(aiUsage.week?.data || []).map((d: any, i: number) => {
                    const max = Math.max(...(aiUsage.week?.data || []).map((x: any) => Number(x.requests) || 0), 1)
                    const height = ((Number(d.requests) || 0) / max) * 100
                    return (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: '100%', height: `${height}%`, minHeight: 4, borderRadius: 4, background: 'linear-gradient(180deg, #7c6ef5, #7c6ef540)' }} />
                        <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.4)', fontFamily: 'ui-monospace, monospace' }}>{d.date?.slice(5)}</span>
                        <span style={{ fontSize: 9, color: '#7c6ef5', fontWeight: 700 }}>{d.requests}</span>
                      </div>
                    )
                  })}
                </div>
                <h4 style={{ fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Top Users Today</h4>
                <div style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', padding: '6px 12px', background: 'rgba(255,255,255,0.03)', fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.4)', fontFamily: 'ui-monospace, monospace' }}>
                    <span style={{ flex: 2 }}>Email</span>
                    <span style={{ flex: 1 }}>Requests</span>
                    <span style={{ flex: 1 }}>Tokens In</span>
                    <span style={{ flex: 1 }}>Tokens Out</span>
                  </div>
                  {(aiUsage.topUsers || []).map((u: any, i: number) => (
                    <div key={i} style={{ display: 'flex', padding: '6px 12px', borderTop: '1px solid rgba(255,255,255,0.04)', fontSize: 11, color: 'rgba(255,255,255,0.7)', fontFamily: 'ui-monospace, monospace' }}>
                      <span style={{ flex: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</span>
                      <span style={{ flex: 1, color: '#7c6ef5', fontWeight: 700 }}>{u.requests}</span>
                      <span style={{ flex: 1 }}>{u.tokens_in}</span>
                      <span style={{ flex: 1 }}>{u.tokens_out}</span>
                    </div>
                  ))}
                  {(aiUsage.topUsers || []).length === 0 && <div style={{ padding: 12, color: 'rgba(255,255,255,0.3)', fontSize: 11, textAlign: 'center' }}>No usage today</div>}
                </div>
              </>
            )}
          </>
        )}

        {tab === 'revenue' && (
          <>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 12 }}>Revenue Overview</h3>
            {!revenue ? <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', padding: 20 }}>Loading revenue data...</div> : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
                  <StatCard label="MRR" value={`$${revenue.mrr}`} color="#5fd4a4" />
                  <StatCard label="Active Subs" value={revenue.activeSubscriptions} color="#7c6ef5" />
                  <StatCard label="Cancelled" value={revenue.cancelledSubscriptions} color="#f07070" />
                  <StatCard label="Failed Payments" value={revenue.failedPayments} color="#f5a157" />
                </div>
                <h4 style={{ fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Plan Breakdown</h4>
                <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
                  <div style={{ padding: '10px 16px', borderRadius: 8, background: 'rgba(124,110,245,0.08)', border: '1px solid rgba(124,110,245,0.2)' }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: '#7c6ef5' }}>{revenue.planBreakdown?.pro || 0}</div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', fontFamily: 'ui-monospace, monospace' }}>Pro ($20/mo)</div>
                  </div>
                  <div style={{ padding: '10px 16px', borderRadius: 8, background: 'rgba(95,212,164,0.08)', border: '1px solid rgba(95,212,164,0.2)' }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: '#5fd4a4' }}>{revenue.planBreakdown?.unlimited || 0}</div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', fontFamily: 'ui-monospace, monospace' }}>Unlimited ($49/mo)</div>
                  </div>
                  <div style={{ padding: '10px 16px', borderRadius: 8, background: 'rgba(255,208,134,0.08)', border: '1px solid rgba(255,208,134,0.2)' }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: '#ffd086' }}>{revenue.planBreakdown?.enterprise || 0}</div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', fontFamily: 'ui-monospace, monospace' }}>Enterprise ($99/mo)</div>
                  </div>
                </div>
                <h4 style={{ fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Monthly Revenue</h4>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120, marginBottom: 24, padding: '0 4px' }}>
                  {(revenue.monthlyRevenue || []).map((m: any, i: number) => {
                    const max = Math.max(...(revenue.monthlyRevenue || []).map((x: any) => x.amount), 1)
                    const height = (m.amount / max) * 100
                    return (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: '100%', height: `${height}%`, minHeight: 4, borderRadius: 4, background: 'linear-gradient(180deg, #5fd4a4, #5fd4a440)' }} />
                        <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.4)', fontFamily: 'ui-monospace, monospace' }}>{m.month?.slice(5)}</span>
                        <span style={{ fontSize: 9, color: '#5fd4a4', fontWeight: 700 }}>${m.amount}</span>
                      </div>
                    )
                  })}
                </div>
                <h4 style={{ fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Recent Payments</h4>
                {(revenue.recentPayments || []).map((p: any, i: number) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>
                    <span style={{ padding: '2px 6px', borderRadius: 4, background: p.status === 'succeeded' ? 'rgba(95,212,164,0.1)' : 'rgba(240,112,112,0.1)', color: p.status === 'succeeded' ? '#5fd4a4' : '#f07070', fontWeight: 700, fontSize: 9 }}>{p.status}</span>
                    <span style={{ color: '#fff', fontWeight: 700 }}>${p.amount}</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9 }}>{p.date ? new Date(p.date).toLocaleDateString() : ''}</span>
                  </div>
                ))}
                {(revenue.recentPayments || []).length === 0 && <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>No payments yet</div>}
              </>
            )}
          </>
        )}

        {tab === 'health' && (
          <>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 12 }}>System Health</h3>
            {!health ? <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', padding: 20 }}>Loading...</div> : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
                  <StatCard label="Status" value={health.status === 'healthy' ? '✓ Healthy' : '⚠ Degraded'} color={health.status === 'healthy' ? '#5fd4a4' : '#f07070'} />
                  <StatCard label="DB Latency" value={`${health.dbLatency}ms`} color="#7c6ef5" />
                  <StatCard label="Uptime" value={health.uptime} color="#38bdf8" />
                  <StatCard label="Errors (24h)" value={health.errorsLast24h} color="#f07070" />
                  <StatCard label="Memory" value={`${health.memory?.used}/${health.memory?.total}MB`} color="#f5a157" />
                  <StatCard label="Node" value={health.nodeVersion} color="#666" />
                </div>
                <h4 style={{ fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Database Tables</h4>
                {dbTable ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <button onClick={() => { setDbTable(null); setDbRows([]) }} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, padding: '3px 10px', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 10 }}>← Back</button>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', fontFamily: 'ui-monospace, monospace' }}>{dbTable}</span>
                      <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>({dbRows.length} rows shown)</span>
                      <div style={{ flex: 1 }} />
                      {['users', 'projects', 'subscriptions', 'payments', 'templates', 'themes', 'teams', 'promo_codes', 'notifications'].includes(dbTable!) && (
                        <button onClick={() => { const cols = dbColumns.filter(c => c !== 'id' && c !== 'created_at' && c !== 'updated_at'); const values: Record<string,string> = {}; cols.forEach(c => { const v = prompt(`Value for ${c}:`); if (v !== null) values[c] = v }); if (Object.keys(values).length > 0) { const colNames = Object.keys(values).join(', '); const colVals = Object.values(values).map(v => `'${v.replace(/'/g, "''")}'`).join(', '); authFetch(apiUrl('/api/enterprise/db/query'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sql: `INSERT INTO "${dbTable}" (id, ${colNames}) VALUES ('${crypto.randomUUID()}', ${colVals})` }) }).then(() => loadTable(dbTable!)).catch(() => {}) } }} style={{ padding: '4px 12px', borderRadius: 4, border: '1px solid rgba(95,212,164,0.3)', background: 'rgba(95,212,164,0.08)', color: '#5fd4a4', fontSize: 10, cursor: 'pointer', fontWeight: 700 }}>+ Add Row</button>
                      )}
                    </div>
                    {dbLoading ? <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>Loading...</div> : (
                      <div style={{ overflow: 'auto', maxHeight: 400, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, fontFamily: 'ui-monospace, monospace' }}>
                          <thead>
                            <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                              {dbColumns.map(col => <th key={col} style={{ padding: '6px 8px', textAlign: 'left', color: 'rgba(255,255,255,0.5)', fontWeight: 700, borderBottom: '1px solid rgba(255,255,255,0.06)', whiteSpace: 'nowrap' }}>{col}</th>)}
                              <th style={{ padding: '6px 8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {dbRows.map((row, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                                {dbColumns.map(col => { const editable = ['users', 'projects', 'subscriptions', 'payments', 'templates', 'themes', 'teams', 'promo_codes', 'notifications'].includes(dbTable!) && col !== 'id' && col !== 'created_at' && col !== 'updated_at'; return <td key={col} onClick={() => { if (!editable) return; const newVal = prompt(`Edit ${col}:`, String(row[col] ?? '')); if (newVal !== null && row.id) { authFetch(apiUrl('/api/enterprise/db/query'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sql: `UPDATE "${dbTable}" SET ${col} = '${newVal.replace(/'/g, "''")}' WHERE id = '${row.id}'` }) }).then(() => loadTable(dbTable!)).catch(() => {}) } }} style={{ padding: '4px 8px', color: 'rgba(255,255,255,0.7)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: editable ? 'pointer' : 'default' }} title={editable ? 'Click to edit' : ''}>{String(row[col] ?? '').slice(0, 50)}</td> })}
                                <td style={{ padding: '4px 8px' }}>{row.id && <button onClick={() => deleteRow(dbTable!, row.id)} style={{ background: 'none', border: '1px solid rgba(240,112,112,0.3)', borderRadius: 3, color: '#f07070', fontSize: 8, padding: '1px 6px', cursor: 'pointer' }}>×</button>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {dbRows.length === 0 && <div style={{ padding: 16, color: 'rgba(255,255,255,0.3)', fontSize: 11, textAlign: 'center' }}>Empty table</div>}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 6 }}>
                    {(health.dbTables || []).map((t: any, i: number) => (
                      <div key={i} onClick={() => loadTable(t.name)} style={{ padding: '8px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', fontSize: 10, fontFamily: 'ui-monospace, monospace', cursor: 'pointer', transition: 'border-color 0.15s' }}
                        onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(124,110,245,0.3)')}
                        onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)')}
                      >
                        <div style={{ color: '#fff', fontWeight: 700 }}>{t.name}</div>
                        <div style={{ color: t.rows > 0 ? '#5fd4a4' : 'rgba(255,255,255,0.3)', marginTop: 2 }}>{t.rows} rows</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {tab === 'deployments' && (
          <>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 12 }}>All Deployments</h3>
            <div style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'flex', padding: '6px 12px', background: 'rgba(255,255,255,0.03)', fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.4)', fontFamily: 'ui-monospace, monospace' }}>
                <span style={{ flex: 2 }}>User</span>
                <span style={{ flex: 1 }}>Status</span>
                <span style={{ flex: 2 }}>URL</span>
                <span style={{ flex: 1 }}>Provider</span>
                <span style={{ flex: 1 }}>Date</span>
              </div>
              {deployments.map((d: any, i: number) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', borderTop: '1px solid rgba(255,255,255,0.04)', fontSize: 10, fontFamily: 'ui-monospace, monospace', color: 'rgba(255,255,255,0.7)' }}>
                  <span style={{ flex: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.email}</span>
                  <span style={{ flex: 1 }}><span style={{ padding: '1px 6px', borderRadius: 4, background: d.status === 'deployed' ? 'rgba(95,212,164,0.1)' : 'rgba(240,112,112,0.1)', color: d.status === 'deployed' ? '#5fd4a4' : '#f07070', fontSize: 8, fontWeight: 700 }}>{d.status}</span></span>
                  <span style={{ flex: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.url ? <a href={d.url} target="_blank" rel="noopener" style={{ color: '#38bdf8', textDecoration: 'none' }}>{d.url.slice(0, 40)}</a> : '-'}</span>
                  <span style={{ flex: 1 }}>{d.provider}</span>
                  <span style={{ flex: 1, fontSize: 9 }}>{d.created_at ? new Date(d.created_at).toLocaleDateString() : ''}</span>
                </div>
              ))}
              {deployments.length === 0 && <div style={{ padding: 16, color: 'rgba(255,255,255,0.3)', fontSize: 11, textAlign: 'center' }}>No deployments</div>}
            </div>
          </>
        )}

        {tab === 'flags' && (
          <>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 12 }}>Feature Flags</h3>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>Feature</span>
                <select value={newFlagName} onChange={e => { setNewFlagName(e.target.value); const f = PULSE_FEATURES.find(x => x.id === e.target.value); if (f) setNewFlagDesc(f.desc) }} style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', background: '#1a1a2e', color: '#e0e0e0', fontSize: 10, fontFamily: 'ui-monospace, monospace', outline: 'none', width: 200 }}>
                  <option value="" style={{ background: '#1a1a2e', color: '#e0e0e0' }}>Select feature...</option>
                  {PULSE_FEATURES.map(f => <option key={f.id} value={f.id} style={{ background: '#1a1a2e', color: '#e0e0e0' }}>{f.label}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>Plans</span>
                <select value={newFlagPlans} onChange={e => setNewFlagPlans(e.target.value)} style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', background: '#1a1a2e', color: '#e0e0e0', fontSize: 10, cursor: 'pointer' }}>
                  <option value="all" style={{ background: '#1a1a2e', color: '#e0e0e0' }}>All plans</option>
                  <option value="pro,unlimited,enterprise" style={{ background: '#1a1a2e', color: '#e0e0e0' }}>Pro+</option>
                  <option value="unlimited,enterprise" style={{ background: '#1a1a2e', color: '#e0e0e0' }}>Unlimited+</option>
                  <option value="enterprise" style={{ background: '#1a1a2e', color: '#e0e0e0' }}>Enterprise only</option>
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>Description</span>
                <input value={newFlagDesc} onChange={e => setNewFlagDesc(e.target.value)} placeholder="What this flag does" style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: '#fff', fontSize: 11, outline: 'none', width: 200 }} />
              </div>
              <button onClick={addFlag} style={{ padding: '6px 14px', borderRadius: 4, border: 'none', background: '#7c6ef5', color: '#fff', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>Add Flag</button>
            </div>
            <div style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden' }}>
              {flags.length === 0 && <div style={{ padding: 16, color: 'rgba(255,255,255,0.3)', fontSize: 11, textAlign: 'center' }}>No feature flags yet</div>}
              {flags.map((f: any) => (
                <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <button onClick={() => toggleFlag(f.name, !f.enabled)} style={{ width: 36, height: 18, borderRadius: 10, border: 'none', background: f.enabled ? '#5fd4a4' : 'rgba(255,255,255,0.1)', cursor: 'pointer', position: 'relative' }}>
                    <span style={{ position: 'absolute', top: 2, left: f.enabled ? 20 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                  </button>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', fontFamily: 'ui-monospace, monospace' }}>{f.name}</div>
                    {f.description && <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{f.description}</div>}
                  </div>
                  <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 4, background: 'rgba(124,110,245,0.1)', color: '#7c6ef5', fontFamily: 'ui-monospace, monospace' }}>{f.plans || 'all'}</span>
                  <button onClick={() => deleteFlag(f.name)} style={{ background: 'none', border: '1px solid rgba(240,112,112,0.3)', borderRadius: 3, color: '#f07070', fontSize: 9, padding: '2px 8px', cursor: 'pointer' }}>×</button>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === 'reports' && (
          <>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 12 }}>User Reports & Flags</h3>
            {reports.length === 0 && <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', padding: 20 }}>No reports yet</div>}
            {reports.map((r: any) => (
              <div key={r.id} style={{ padding: '12px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)', marginBottom: 8, background: r.status === 'pending' ? 'rgba(245,161,87,0.04)' : 'rgba(255,255,255,0.01)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: r.status === 'pending' ? 'rgba(245,161,87,0.15)' : r.status === 'resolved' ? 'rgba(95,212,164,0.15)' : 'rgba(240,112,112,0.15)', color: r.status === 'pending' ? '#f5a157' : r.status === 'resolved' ? '#5fd4a4' : '#f07070' }}>{r.status}</span>
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontFamily: 'ui-monospace, monospace' }}>{r.reason}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)' }}>{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</span>
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>
                  <span style={{ color: '#38bdf8' }}>Reporter:</span> {r.reporter_email || r.reporter_id?.slice(0, 8)}
                  {r.reported_email && <> → <span style={{ color: '#f07070' }}>Reported:</span> {r.reported_email}</>}
                </div>
                {r.reported_content && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', padding: '4px 8px', background: 'rgba(0,0,0,0.2)', borderRadius: 4, marginBottom: 6, fontFamily: 'ui-monospace, monospace' }}>{r.reported_content.slice(0, 200)}</div>}
                {r.admin_note && <div style={{ fontSize: 9, color: '#5fd4a4', marginBottom: 4 }}>Admin: {r.admin_note}</div>}
                {r.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => resolveReport(r.id, 'resolved')} style={{ padding: '3px 10px', borderRadius: 4, border: '1px solid rgba(95,212,164,0.3)', background: 'rgba(95,212,164,0.08)', color: '#5fd4a4', fontSize: 9, cursor: 'pointer', fontWeight: 700 }}>Resolve</button>
                    <button onClick={() => resolveReport(r.id, 'dismissed')} style={{ padding: '3px 10px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', background: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 9, cursor: 'pointer' }}>Dismiss</button>
                    <button onClick={() => { if (r.reported_user_id) banUser(r.reported_user_id); resolveReport(r.id, 'banned') }} style={{ padding: '3px 10px', borderRadius: 4, border: '1px solid rgba(240,112,112,0.3)', background: 'rgba(240,112,112,0.08)', color: '#f07070', fontSize: 9, cursor: 'pointer', fontWeight: 700 }}>Ban User</button>
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {tab === 'promos' && (
          <>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 12 }}>Promo Codes</h3>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>Code</span>
                <input value={newPromoCode} onChange={e => setNewPromoCode(e.target.value.toUpperCase())} placeholder="LAUNCH20" style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: '#fff', fontSize: 11, fontFamily: 'ui-monospace, monospace', outline: 'none', width: 120 }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>Discount %</span>
                <input value={newPromoDiscount} onChange={e => setNewPromoDiscount(e.target.value)} type="number" style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: '#fff', fontSize: 11, fontFamily: 'ui-monospace, monospace', outline: 'none', width: 70 }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>Max Uses</span>
                <input value={newPromoMaxUses} onChange={e => setNewPromoMaxUses(e.target.value)} type="number" style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: '#fff', fontSize: 11, fontFamily: 'ui-monospace, monospace', outline: 'none', width: 70 }} />
              </div>
              <button onClick={addPromo} style={{ padding: '6px 14px', borderRadius: 4, border: 'none', background: '#7c6ef5', color: '#fff', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>Create</button>
            </div>
            <div style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'flex', padding: '6px 12px', background: 'rgba(255,255,255,0.03)', fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.4)', fontFamily: 'ui-monospace, monospace' }}>
                <span style={{ flex: 1 }}>Code</span>
                <span style={{ width: 80 }}>Discount</span>
                <span style={{ width: 80 }}>Uses</span>
                <span style={{ width: 100 }}>Expires</span>
                <span style={{ width: 50 }}></span>
              </div>
              {promos.length === 0 && <div style={{ padding: 16, color: 'rgba(255,255,255,0.3)', fontSize: 11, textAlign: 'center' }}>No promo codes yet</div>}
              {promos.map((p: any) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderTop: '1px solid rgba(255,255,255,0.04)', fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>
                  <span style={{ flex: 1, color: '#ffd086', fontWeight: 700 }}>{p.code}</span>
                  <span style={{ width: 80, color: '#5fd4a4' }}>{p.discount_percent || p.discount_pct || 0}% off</span>
                  <span style={{ width: 80, color: 'rgba(255,255,255,0.6)' }}>{p.uses || 0}/{p.max_uses || '∞'}</span>
                  <span style={{ width: 100, color: 'rgba(255,255,255,0.4)', fontSize: 9 }}>{p.expires_at ? new Date(p.expires_at).toLocaleDateString() : 'Never'}</span>
                  <span style={{ width: 50 }}><button onClick={() => deletePromo(p.id)} style={{ background: 'none', border: '1px solid rgba(240,112,112,0.3)', borderRadius: 3, color: '#f07070', fontSize: 9, padding: '1px 6px', cursor: 'pointer' }}>×</button></span>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === 'tickets' && (
          <>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 4 }}>Support Tickets</h3>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>Open: {tickets.filter(t => t.status === 'open' || t.state === 'new').length} · In Progress: {tickets.filter(t => t.state === 'in_progress').length} · Resolved: {tickets.filter(t => t.state === 'resolved' || t.state === 'closed').length}</p>
            {tickets.length === 0 && <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', padding: 20 }}>No support tickets yet</div>}
            <div style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'flex', padding: '6px 12px', background: 'rgba(255,255,255,0.03)', fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.4)', fontFamily: 'ui-monospace, monospace' }}>
                <span style={{ width: 80 }}>Number</span>
                <span style={{ flex: 2 }}>Subject</span>
                <span style={{ flex: 1 }}>Caller</span>
                <span style={{ width: 70 }}>Priority</span>
                <span style={{ width: 80 }}>State</span>
                <span style={{ width: 70 }}>Category</span>
                <span style={{ width: 90 }}>Assigned</span>
                <span style={{ width: 90 }}>Created</span>
              </div>
              {tickets.map((t: any) => (
                <div key={t.id} onClick={() => setSelectedTicket(t)} style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderTop: '1px solid rgba(255,255,255,0.04)', fontSize: 10, fontFamily: 'ui-monospace, monospace', cursor: 'pointer', transition: 'background 0.1s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(124,110,245,0.05)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <span style={{ width: 80, color: '#7c6ef5', fontWeight: 700 }}>{t.ticket_number || t.id?.slice(0, 8)}</span>
                  <span style={{ flex: 2, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</span>
                  <span style={{ flex: 1, color: 'rgba(255,255,255,0.5)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.user_email}</span>
                  <span style={{ width: 70 }}><span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 8, fontWeight: 700, background: t.priority === 'urgent' || t.priority === 'critical' ? 'rgba(240,112,112,0.15)' : t.priority === 'high' ? 'rgba(245,161,87,0.15)' : 'rgba(255,255,255,0.05)', color: t.priority === 'urgent' || t.priority === 'critical' ? '#f07070' : t.priority === 'high' ? '#f5a157' : 'rgba(255,255,255,0.4)' }}>{t.priority}</span></span>
                  <span style={{ width: 80 }}><span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 8, fontWeight: 700, background: t.state === 'new' ? 'rgba(245,161,87,0.15)' : t.state === 'in_progress' ? 'rgba(56,189,248,0.15)' : 'rgba(95,212,164,0.15)', color: t.state === 'new' ? '#f5a157' : t.state === 'in_progress' ? '#38bdf8' : '#5fd4a4' }}>{t.state || t.status}</span></span>
                  <span style={{ width: 70, color: 'rgba(255,255,255,0.4)' }}>{t.category}</span>
                  <span style={{ width: 90, color: t.assigned_to ? '#38bdf8' : 'rgba(255,255,255,0.2)', fontSize: 9 }}>{t.assigned_to || 'unassigned'}</span>
                  <span style={{ width: 90, color: 'rgba(255,255,255,0.3)', fontSize: 9 }}>{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</span>
                </div>
              ))}
            </div>
            {selectedTicket && <TicketModal ticket={selectedTicket} onClose={() => setSelectedTicket(null)} onUpdate={loadTickets} />}
          </>
        )}

        {tab === 'announce' && (
          <>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 12 }}>Send Announcement</h3>
            <div style={{ maxWidth: 500, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input value={announceTitle} onChange={e => setAnnounceTitle(e.target.value)} placeholder="Title" style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: '#fff', fontSize: 12, fontFamily: 'ui-monospace, monospace', outline: 'none' }} />
              <textarea value={announceBody} onChange={e => setAnnounceBody(e.target.value)} placeholder="Body (optional)" rows={3} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: '#fff', fontSize: 12, fontFamily: 'ui-monospace, monospace', outline: 'none', resize: 'none' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>Target:</span>
                <select value={announcePlan} onChange={e => setAnnouncePlan(e.target.value)} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: '#fff', fontSize: 10, cursor: 'pointer' }}>
                  <option value="all">All users</option>
                  <option value="free">Free only</option>
                  <option value="pro">Pro only</option>
                  <option value="unlimited">Unlimited only</option>
                  <option value="enterprise">Enterprise only</option>
                </select>
                <button onClick={sendAnnouncement} style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: '#7c6ef5', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Send</button>
              </div>
            </div>
          </>
        )}

        {tab === 'audit' && (
          <>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 12 }}>Audit Logs</h3>
            {auditLogs.length === 0 && <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', padding: 20 }}>No audit logs yet</div>}
            {auditLogs.map((log: any, i: number) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 10, fontFamily: 'ui-monospace, monospace' }}>
                <span style={{ padding: '2px 6px', borderRadius: 4, background: 'rgba(95,212,164,0.1)', color: '#5fd4a4', fontWeight: 700, fontSize: 9 }}>{log.action}</span>
                <span style={{ color: 'rgba(255,255,255,0.4)', width: 70 }}>{log.user_id?.slice(0, 8)}</span>
                <span style={{ flex: 1, color: 'rgba(255,255,255,0.3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.metadata ? JSON.stringify(JSON.parse(log.metadata || '{}'), null, 0).slice(0, 60) : ''}</span>
                <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9 }}>{new Date(log.created_at).toLocaleString()}</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  )
}

function StatCard({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div style={{ padding: '16px 14px', borderRadius: 10, border: `1px solid ${color}30`, background: 'rgba(255,255,255,0.02)', textAlign: 'center' }}>
      <div style={{ fontSize: 24, fontWeight: 900, color }}>{value}</div>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 4, fontFamily: 'ui-monospace, monospace' }}>{label}</div>
    </div>
  )
}
